/**
 * Interactive US State Map — shows user density by state.
 * Simplified SVG paths for all 50 states + DC.
 */
import { useState } from "react";

// Abbreviated state paths (simplified boundaries)
// Format: { abbr: "XX", name: "State Name", x, y } — using centroid positions for label/hover
const STATE_POSITIONS = {
  AL: { name: "Alabama", x: 730, y: 430 },
  AK: { name: "Alaska", x: 161, y: 553 },
  AZ: { name: "Arizona", x: 230, y: 420 },
  AR: { name: "Arkansas", x: 625, y: 410 },
  CA: { name: "California", x: 115, y: 350 },
  CO: { name: "Colorado", x: 330, y: 320 },
  CT: { name: "Connecticut", x: 895, y: 210 },
  DE: { name: "Delaware", x: 870, y: 280 },
  FL: { name: "Florida", x: 800, y: 520 },
  GA: { name: "Georgia", x: 775, y: 440 },
  HI: { name: "Hawaii", x: 310, y: 565 },
  ID: { name: "Idaho", x: 210, y: 190 },
  IL: { name: "Illinois", x: 660, y: 300 },
  IN: { name: "Indiana", x: 700, y: 300 },
  IA: { name: "Iowa", x: 590, y: 250 },
  KS: { name: "Kansas", x: 490, y: 340 },
  KY: { name: "Kentucky", x: 740, y: 340 },
  LA: { name: "Louisiana", x: 640, y: 480 },
  ME: { name: "Maine", x: 920, y: 120 },
  MD: { name: "Maryland", x: 850, y: 280 },
  MA: { name: "Massachusetts", x: 910, y: 195 },
  MI: { name: "Michigan", x: 710, y: 210 },
  MN: { name: "Minnesota", x: 550, y: 160 },
  MS: { name: "Mississippi", x: 680, y: 450 },
  MO: { name: "Missouri", x: 610, y: 340 },
  MT: { name: "Montana", x: 300, y: 130 },
  NE: { name: "Nebraska", x: 460, y: 270 },
  NV: { name: "Nevada", x: 170, y: 300 },
  NH: { name: "New Hampshire", x: 910, y: 165 },
  NJ: { name: "New Jersey", x: 880, y: 255 },
  NM: { name: "New Mexico", x: 310, y: 420 },
  NY: { name: "New York", x: 860, y: 195 },
  NC: { name: "North Carolina", x: 820, y: 370 },
  ND: { name: "North Dakota", x: 440, y: 140 },
  OH: { name: "Ohio", x: 745, y: 275 },
  OK: { name: "Oklahoma", x: 510, y: 390 },
  OR: { name: "Oregon", x: 135, y: 170 },
  PA: { name: "Pennsylvania", x: 830, y: 240 },
  RI: { name: "Rhode Island", x: 910, y: 210 },
  SC: { name: "South Carolina", x: 810, y: 410 },
  SD: { name: "South Dakota", x: 440, y: 200 },
  TN: { name: "Tennessee", x: 730, y: 380 },
  TX: { name: "Texas", x: 480, y: 470 },
  UT: { name: "Utah", x: 250, y: 310 },
  VT: { name: "Vermont", x: 890, y: 155 },
  VA: { name: "Virginia", x: 830, y: 320 },
  WA: { name: "Washington", x: 155, y: 110 },
  WV: { name: "West Virginia", x: 790, y: 310 },
  WI: { name: "Wisconsin", x: 620, y: 190 },
  WY: { name: "Wyoming", x: 320, y: 230 },
  DC: { name: "Washington DC", x: 860, y: 290 },
};

function getColor(count, maxCount) {
  if (!count || count === 0) return "rgba(255,255,255,0.03)";
  var intensity = Math.min(count / Math.max(maxCount, 1), 1);
  // Gradient from dim coral to bright coral
  var alpha = 0.15 + intensity * 0.7;
  return `rgba(255, 107, 107, ${alpha})`;
}

function getBorderColor(count) {
  if (!count || count === 0) return "rgba(255,255,255,0.08)";
  return "rgba(255, 107, 107, 0.5)";
}

export function USStateMap({ stateData, onStateClick }) {
  const [hovered, setHovered] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  // Build lookup: state abbr -> data
  var dataMap = {};
  var maxCount = 0;
  (stateData || []).forEach(function(s) {
    dataMap[s.state] = s;
    if (s.count > maxCount) maxCount = s.count;
  });

  function handleMouseEnter(abbr, e) {
    setHovered(abbr);
    var rect = e.currentTarget.closest("svg").getBoundingClientRect();
    setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top - 10 });
  }

  function handleMouseMove(e) {
    if (hovered) {
      var rect = e.currentTarget.closest("svg").getBoundingClientRect();
      setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top - 10 });
    }
  }

  var hoveredData = hovered ? dataMap[hovered] : null;

  return (
    <div className="relative" data-testid="us-state-map">
      <svg
        viewBox="60 80 900 520"
        className="w-full h-auto"
        style={{ maxHeight: "400px" }}
        onMouseMove={handleMouseMove}
      >
        {/* State circles */}
        {Object.entries(STATE_POSITIONS).map(function([abbr, pos]) {
          var data = dataMap[abbr];
          var count = data ? data.count : 0;
          var baseRadius = 16;
          var radius = count > 0 ? baseRadius + Math.min(count * 2, 12) : baseRadius;

          return (
            <g
              key={abbr}
              onMouseEnter={function(e) { handleMouseEnter(abbr, e); }}
              onMouseLeave={function() { setHovered(null); }}
              onClick={function() { if (onStateClick && count > 0) onStateClick(abbr); }}
              style={{ cursor: count > 0 ? "pointer" : "default" }}
            >
              <circle
                cx={pos.x}
                cy={pos.y}
                r={radius}
                fill={getColor(count, maxCount)}
                stroke={getBorderColor(count)}
                strokeWidth={hovered === abbr ? 2 : 1}
                className="transition-all duration-150"
              />
              <text
                x={pos.x}
                y={pos.y + 1}
                textAnchor="middle"
                dominantBaseline="middle"
                className="select-none pointer-events-none"
                style={{
                  fontSize: count > 0 ? "11px" : "9px",
                  fontWeight: count > 0 ? "700" : "500",
                  fill: count > 0 ? "#fff" : "rgba(255,255,255,0.25)",
                }}
              >
                {abbr}
              </text>
              {count > 0 && (
                <text
                  x={pos.x}
                  y={pos.y + 12}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="select-none pointer-events-none"
                  style={{ fontSize: "8px", fontWeight: "600", fill: "rgba(255,255,255,0.7)" }}
                >
                  {count}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* Tooltip */}
      {hovered && (
        <div
          className="absolute z-50 bg-[#1a1a1a] border border-white/20 rounded-md px-3 py-2 text-sm pointer-events-none shadow-xl"
          style={{
            left: tooltipPos.x,
            top: tooltipPos.y,
            transform: "translate(-50%, -100%)",
          }}
          data-testid="map-tooltip"
        >
          <div className="font-bold text-white">{STATE_POSITIONS[hovered]?.name || hovered}</div>
          {hoveredData ? (
            <>
              <div className="text-white/60 text-xs mt-0.5">
                {hoveredData.count} user{hoveredData.count !== 1 ? "s" : ""}
                {hoveredData.studios > 0 && <span> ({hoveredData.studios} studio{hoveredData.studios !== 1 ? "s" : ""})</span>}
              </div>
              {hoveredData.cities && hoveredData.cities.length > 0 && (
                <div className="text-white/40 text-xs mt-1 border-t border-white/10 pt-1">
                  {hoveredData.cities.slice(0, 3).map(function(c) {
                    return <div key={c.city}>{c.city}: {c.count}</div>;
                  })}
                </div>
              )}
            </>
          ) : (
            <div className="text-white/40 text-xs">No users</div>
          )}
        </div>
      )}
    </div>
  );
}
